<script setup lang="ts">
import { useId } from 'vue';
import type { CtaDescriptor } from '~/utils/event-policy';

const props = defineProps<{
  cta: CtaDescriptor;
  eventId: string;
}>();

const emit = defineEmits<{
  commit: [];
  purchase: [];
  withdraw: [];
}>();

const route = useRoute();

function handleClick() {
  switch (props.cta.action) {
    case 'login':
      navigateTo(
        `/login?redirect_to=${encodeURIComponent(route.path)}` +
          `&event_id=${encodeURIComponent(props.eventId)}`,
      );
      break;
    case 'commit':
      emit('commit');
      break;
    case 'purchase':
      emit('purchase');
      break;
    case 'withdraw':
      emit('withdraw');
      break;
  }
}

const vuetifyVariant = computed(() => {
  switch (props.cta.variant) {
    case 'primary':
      return 'flat';
    case 'outlined':
      return 'outlined';
    case 'text':
      return 'text';
    default:
      return 'flat';
  }
});

const vuetifyColor = computed(() =>
  props.cta.variant === 'primary' ? 'primary' : undefined,
);

// The disabled reason is rendered as always-visible caption text (not a
// hover-only tooltip) so touch users see it too. It is linked to the button
// via aria-describedby so assistive tech announces it with the button.
const reasonId = `event-cta-reason-${useId()}`;
const disabledReason = computed(() =>
  props.cta.disabled && props.cta.disabledReason
    ? props.cta.disabledReason
    : null,
);
</script>

<template>
  <div v-if="cta.label || cta.action !== 'none'" class="event-cta-module">
    <v-btn
      block
      rounded
      :variant="vuetifyVariant"
      :color="vuetifyColor"
      :disabled="cta.disabled"
      :aria-describedby="disabledReason ? reasonId : undefined"
      @click="handleClick"
    >
      {{ cta.label }}
    </v-btn>
    <p
      v-if="disabledReason"
      :id="reasonId"
      class="text-body-2 text-medium-emphasis mt-2"
      data-testid="cta-disabled-reason"
    >
      {{ disabledReason }}
    </p>
  </div>
</template>
